import { useEffect } from "react";

import {
  OTTO_TOKEN_REFRESH_REQUEST,
  refreshOttoToken,
} from "./tokenRefresh.js";

/**
 * Keep the embedded Staff Otto widget's session token fresh so a
 * long-open dashboard never sends an expired token (the "Gateway HTTP
 * 401" bug — see `tokenRefresh.ts`).
 *
 * Three triggers, belt-and-suspenders:
 *   1. A periodic re-mint well inside the token TTL. Interval timers
 *      only fire for foreground tabs, which is exactly when the user can
 *      be chatting — a backgrounded tab is covered by (2).
 *   2. `visibilitychange` → the tab regaining focus re-mints immediately,
 *      so a token that lapsed while the tab was hidden is refreshed
 *      BEFORE the user can send a message.
 *   3. `otto:token-refresh-request` from the widget — chat.js asks for a
 *      fresh token when the gateway 401s an expired one, then replays the
 *      message once it arrives. This self-heals any gap the timers miss.
 *
 * No-op when `ottoOrigin` is null (widget not configured, e.g. dev
 * without VITE_OTTO_BASE_URL).
 */
const REFRESH_INTERVAL_MS = 40 * 60 * 1000; // 40 min — comfortably inside the token TTL

export function useOttoTokenRefresh(ottoOrigin: string | null): void {
  useEffect(() => {
    if (!ottoOrigin) return;

    const interval = window.setInterval(() => {
      void refreshOttoToken(ottoOrigin);
    }, REFRESH_INTERVAL_MS);

    const onVisible = (): void => {
      if (document.visibilityState === "visible") {
        void refreshOttoToken(ottoOrigin);
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    const onMessage = (e: MessageEvent): void => {
      // Only trust refresh requests coming from the Otto host origin.
      if (e.origin !== ottoOrigin) return;
      const type = (e.data as { type?: string } | null)?.type;
      if (type === OTTO_TOKEN_REFRESH_REQUEST) {
        void refreshOttoToken(ottoOrigin);
      }
    };
    window.addEventListener("message", onMessage);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("message", onMessage);
    };
  }, [ottoOrigin]);
}
